import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  HostListener,
  SimpleChanges,
} from '@angular/core';
import { Subscription, of, switchMap, take } from 'rxjs';
import {
  PanelBodyComponent,
  PanelContainerComponent,
  PanelHeaderComponent,
} from 'src/app/shared/content-panels';
import { TableColumnPipe } from 'src/app/shared/pipes/table-column.pipe';
import { SubmissionView } from '../models/submission-view.model';
import { SubmissionsService } from '../services/submissions.service';
import { DynamicFormControl, FormControlFactory } from 'src/app/shared/forms';
import { FilterService } from 'src/app/shared/filtering';
import {
  MatIconModule,
  MatPaginatorModule,
  MatSortModule,
  MatTableDataSource,
  MatTableModule,
  MatTooltip,
  PageEvent,
  Sort,
} from 'src/app/shared/material';
import { MatMenuModule } from '@angular/material/menu';
import { DrawerService, SpinnerService } from 'src/app/shared/services';
import { BreakpointService } from 'src/app/shared/services/breakpoint.service';
import { Submission } from '../models';
import {
  SearchBarComponent,
  SearchField,
} from '../../../shared/filtering/components/search-bar/search-bar.component';
import { SubmissionConstants } from '../models/constants/submissions-constants';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { DialogType } from 'src/app/shared/models/dialog-type.enum';
import { UsStateService } from 'src/app/shared/forms/services/form-us-state.service';
import { SubmissionsDownloadService } from '../services/submissions-download.service';

@Component({
    selector: 'app-submissions-list-view',
    imports: [
        CommonModule,
        PanelBodyComponent,
        PanelContainerComponent,
        PanelHeaderComponent,
        SearchBarComponent,
        MatIconModule,
        MatTableModule,
        MatPaginatorModule,
        MatMenuModule,
        MatTooltip,
        MatSortModule,
    ],
    providers: [TableColumnPipe],
    templateUrl: './submissions-list-view.component.html',
    styleUrl: './submissions-list-view.component.scss'
})
export class SubmissionsListViewComponent {
  @Input() isDetailOpen = false;
  @Input() activeSubmission: SubmissionView | null = null;
  @Input() materialPageIndex: number = 0; // Needed for Material, 0-based page index
  @Input() shouldClearSearch: boolean = false;
  @Output() selectedSubmission = new EventEmitter<SubmissionView>();
  @Output() selectedManualSub = new EventEmitter<SubmissionView>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() searchChange = new EventEmitter<{
    field: string;
    value: string;
  } | null>();

  submission: SubmissionView;
  tableColumns = Object.values(this.subConstants.tableColumns);
  searchFields: SearchField[] = Object.entries(
    this.subConstants.tableColumnLabels,
  ).map(([value, displayName]) => ({ value, displayName }));
  activeSearch: { field: string; value: string } | null = null;

  paginatorOptions = {
    paging: true,
    show: false,
    pageSizeOptions: [20, 50, 100],
    pageSize: 20,
    pageLength: 0,
    showFirstLastButtons: true,
  };

  dataSource = new MatTableDataSource<SubmissionView>([]);
  isEditMode = false;

  //used to check against 90 days
  soldToolTip: string =
    'This Credit Submission has a sold date older than 90 days and may need your attention';

  //used to check against 90 days
  heatPumpInvoiceAvailableToolTip: string =
    this.subConstants.heatPumpInvoiceAvailableToolTip;

  //variables for screensize changes
  isSmallScreen: boolean = false;
  getScreenWidth: number;
  getScreenHeight: number;
  isBelow1150: boolean = false;
  isMobile: boolean = false;
  isBelow1050: boolean = false;

  private _subscriptions: Subscription[] = [];
  private _manualSubSubscriptions: Subscription[] = [];
  private _submissionCollection: SubmissionView[] = [];
  private _filterControls: DynamicFormControl[];
  stateValues: any[] = [];

  constructor(
    public drawerSvc: DrawerService,
    public subConstants: SubmissionConstants,
    public filterSvc: FilterService,
    private submissionsSrv: SubmissionsService,
    private filterCtrlFactory: FormControlFactory,
    private dialogService: DialogService,
    private breakPointService: BreakpointService,
    private spinnerService: SpinnerService,
    private submissionsDownloadService: SubmissionsDownloadService,
    public stateService: UsStateService,
    private tableColumnPipe: TableColumnPipe,
  ) {}

  //listens for a screen size change for displaying nav-bar or nav-bar-mobile
  @HostListener('window:resize')
  onWindowResize() {
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
    this.isSmallScreen = this.breakPointService.smallScreenCheck(
      this.getScreenHeight,
      this.getScreenWidth,
    );
    this.isBelow1150 = this.breakPointService.below1150Check(
      this.getScreenHeight,
      this.getScreenWidth,
    );
    this.isMobile = this.breakPointService.mobileCheck(
      this.getScreenHeight,
      this.getScreenWidth,
    );
  }

  ngOnInit() {
    this.stateService.getAllStates().subscribe((data) => {
      const sortedStates = [...data];
      sortedStates.sort((a, b) => a.Name.localeCompare(b.Name));
      this.stateValues = sortedStates.map((state) => {
        return { value: state.StateCode, label: state.Name };
      });
      const stateControl = this._filterControls?.find(
        (c) => c.propertyBinding === 'HomeState',
      );
      if (stateControl) {
        stateControl.customOptions = {
          ...stateControl.customOptions,
          selectOptions: [...this.stateValues],
        };
        this.filterSvc.setFilterFormControls(this._filterControls);
      }
    });
    //check screen size for menu and styling changes
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
    this.isSmallScreen = this.breakPointService.smallScreenCheck(
      this.getScreenHeight,
      this.getScreenWidth,
    );
    this.isBelow1150 = this.breakPointService.below1150Check(
      this.getScreenHeight,
      this.getScreenWidth,
    );
    this.isMobile = this.breakPointService.mobileCheck(
      this.getScreenHeight,
      this.getScreenWidth,
    );

    this._filterControls = this.buildControls();
    this.filterSvc.setFilterFormControls(this._filterControls);

    let sub = this.submissionsSrv.submissions$.subscribe((i) => {
      this._submissionCollection = i.Items;
      this.dataSource.data = i.Items;
      this.paginatorOptions.pageLength = i.TotalCount;
    });
    this._subscriptions.push(sub);

    sub = this.submissionsSrv.facilities$
      .pipe(
        switchMap((result) => {
          return of(
            this.filterCtrlFactory.createMultiSelectBox(
              'Facilities',
              'Facilities',
              null,
              {
                placeholder: 'Select a Facility',
                groupName: 'selectFilters',
                selectOptions: result.flatMap((i) => ({
                  value: i.Id,
                  label: i.KeyChain.LegacyId,
                })),
              },
            ),
          );
        }),
      )
      .subscribe((filter) => {
        if (
          this._filterControls
            .map((i) => i.propertyBinding)
            .indexOf(filter.propertyBinding) === -1
        ) {
          this._filterControls.push(filter);
          this.filterSvc.setFilterFormControls(this._filterControls);
        }
      });
    this._subscriptions.push(sub);

  }

  ngOnDestroy() {
    this._subscriptions.forEach((i) => {
      if (i) {
        i.unsubscribe();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['shouldClearSearch'] && this.shouldClearSearch) {
      this.onSearch(null);
      this.submissionsSrv.getSubmissions({
        paging: true,
        materialPageIndex: 0,
        pageNumber: 1,
        pageSize: 20,
      });
    }
  }

  rowSelected(id: number | string) {
    let submission = this._submissionCollection.find((i) => i.Id === id);
    this.isDetailOpen = true;
    this.submission = submission;
    this.selectedSubmission.emit(submission);
  }

  manualSubmissionSelected(id: number | string) {
    let submission = this._submissionCollection.find((i) => i.Id === id);
    this.submission = submission;
    this.selectedManualSub.emit(submission);
  }

  /***
   * Checks SoldDate in table against 90 days
   * returns true if SoldDate is older than 90 days
   * to add notifier icon in the column
   */
  isOlderThan90Days(soldDate: Date) {
    const cutOffDate = new Date();
    cutOffDate.setDate(new Date().getDate() - 90);
    return soldDate && new Date(soldDate) < cutOffDate;
  }

  private buildControls = (): DynamicFormControl[] => [
    this.filterCtrlFactory.createTextBox('Order #', 'OrderNumber', null, {
      placeholder: 'Enter an Order Number',
      groupName: 'textFilters',
    }),
    this.filterCtrlFactory.createTextBox('Serial #', 'SerialNumber', null, {
      placeholder: 'Enter a Serial Number',
      groupName: 'textFilters',
    }),
    this.filterCtrlFactory.createTextBox('Retailer', 'RetailerName', null, {
      placeholder: 'Retailer',
      groupName: 'textFilters',
    }),
    this.filterCtrlFactory.createSelectBox(
      'Furnace Type',
      'FurnaceType',
      null,
      {
        placeholder: 'Select an option',
        groupName: 'selectFilters',
        selectOptions: [
          {
            value: 'Elec w/ Heat Pump',
            label: 'Elec w/ Heat Pump',
          },
          {
            value: 'Elec',
            label: 'Elec',
          },
          {
            value: 'Gas',
            label: 'Gas',
          },
          {
            value: 'Omitted',
            label: 'Omitted',
          },

          {
            value: '(Not Selected)',
            label: '(Not Selected)',
          },
        ],
      },
    ),
    this.filterCtrlFactory.createSelectBox(
      'Heat Pump Invoice Available',
      'hasHeatPumpInvoice',
      null,
      {
        placeholder: 'Select an option',
        groupName: 'selectFilters',
        selectOptions: [
          {
            value: 'true',
            label: 'Yes',
          },
          {
            value: 'false',
            label: 'No',
          },
        ],
      },
    ),
    this.filterCtrlFactory.createTextBox('Customer', 'CustomerName', null, {
      placeholder: 'Enter a Customer Name',
      groupName: 'textFilters',
    }),
    this.filterCtrlFactory.createTextBox('City', 'HomeCity', null, {
      placeholder: 'Enter a City Name',
      groupName: 'customerInfo',
    }),
    this.filterCtrlFactory.createSelectBox('State', 'HomeState', null, {
      placeholder: 'Enter a State Name',
      groupName: 'customerInfo',
      selectOptions: [...this.stateValues],
    }),
    this.filterCtrlFactory.createTextBox(
      'Postal Code',
      'HomePostalCode',
      null,
      {
        placeholder: 'Enter a Postal Code',
        groupName: 'customerInfo',
      },
    ),
    this.filterCtrlFactory.createDateRangePicker(
      'Sold Date',
      'SoldDate',
      null,
      { placeholder: 'Enter a Sold Date range', groupName: 'dateFilters' },
    ),
    this.filterCtrlFactory.createDateRangePicker(
      'Submission Create Date',
      'SubmissionCreateDate',
      null,
      { placeholder: 'Enter a Create Date range', groupName: 'dateFilters' },
    ),
    this.filterCtrlFactory.createMultiSelectBox('Status', 'Status', null, {
      placeholder: 'Select a Status',
      groupName: 'selectFilters',
      selectOptions: [
        {
          value: 1,
          label: 'Not Eligible',
        },
        {
          value: 2,
          label: 'Needs Attention',
        },
        {
          value: 3,
          label: 'In Progress',
        },
        {
          value: 4,
          label: 'Ready To Submit',
        },
        {
          value: 5,
          label: 'Submitted',
        },
        {
          value: 6,
          label: 'Submitted Manually',
        },
        {
          value: 7,
          label: 'Error',
        },
      ],
    }),
    this.filterCtrlFactory.createMultiSelectBox('Version', 'Version', null, {
      placeholder: 'Select a Version',
      groupName: 'selectFilters',
      selectOptions: [
        {
          value: 1,
          label: '2',
        },
        {
          value: 2,
          label: '3',
        },
      ],
    }),
    this.filterCtrlFactory.createSelectBox(
      'Certificate Mailed',
      'isCertificateMailed',
      null,
      {
        placeholder: 'Select an option',
        groupName: 'selectFilters',
        selectOptions: [
          {
            value: 'true',
            label: 'Yes',
          },
          {
            value: 'false',
            label: 'No',
          },
        ],
      },
    ),
  ];

  downloadExcelFile() {
    const filterObj: Record<string, any> = this.filterSvc.activeFilters
      ? { ...this.filterSvc.convertToQueryFilter(this.filterSvc.activeFilters) }
      : {};

    if (this.activeSearch) {
      filterObj['searchField'] = this.activeSearch.field;
      filterObj['searchValue'] = this.activeSearch.value;
      filterObj['IsSearchMode'] = true;
    }

    this.submissionsDownloadService.downloadSubmissions(
      Object.keys(filterObj).length > 0 ? filterObj : null,
    );
  }

  onSearch(searchEvent: { field: string; value: string } | null) {
    this.activeSearch = searchEvent;

    if (searchEvent) {
      this.searchChange.emit(searchEvent);
    } else {
      this.searchChange.emit(null);
    }
  }
  onPageChangeEvent($event: any) {
    this.paginatorOptions = { ...this.paginatorOptions, ...$event };
    this.pageChange.emit($event);
  }

  formatColumnHeader(column: string): string {
    return (
      this.subConstants.tableColumnLabels[column] ||
      this.tableColumnPipe.transform(column)
    );
  }

  /*
   * Used to format the dates displayed in order to avoid time-zone inconsistencies
   * Found that when fetching dates from the DB, depending on the time zone the user is in, they could be displayed incorrectly
   * For example: 2025-01-01 00:00:00+00 (Jan 1 2025) in the DB would be displayed as 2024-12-31 00:00:00+00 (Dec 31, 2024) if the user is in central time zone
   */
  formatDateUTC(input: Date | string): string {
    const date: Date = typeof input === 'string' ? new Date(input) : input;

    return !date || isNaN(date.getTime())
      ? ''
      : Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC',
        }).format(date);
  }
  openDialog() {
    this._manualSubSubscriptions.forEach((i) => {
      if (i) {
        i.unsubscribe();
      }
    });
    const dialogRef = this.dialogService.openCustomDialog(
      DialogType.MANUAL_SUBMISSION,
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const manualSubscription = this.submissionsSrv.submissions$
          .pipe(take(1))
          .subscribe((sub) => {
            this.manualSubmissionSelected(result.submissionId);
          });
        this._manualSubSubscriptions.push(manualSubscription);
        this.submissionsSrv.getSubmissions({
          paging: true,
          materialPageIndex: 0,
          pageNumber: 1,
          pageSize: 20,
          searchField: 'SerialNumber',
          searchValue: result.longSerialNumber,
          IsSearchMode: true,
        });
      }
    });
  }
}
